import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Sparkles,
  Search,
  Loader2,
  Check,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";
import {
  getDemoMode,
  getDemoProfile,
  createAssessment,
  updateAssessment,
} from "../lib/api";

const complexityStyle = (lvl) => {
  switch ((lvl || "").toLowerCase()) {
    case "low":
      return "bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]";
    case "medium":
      return "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]";
    case "high":
      return "bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]";
    case "very high":
      return "bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]";
    default:
      return "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]";
  }
};

const scenarioLabel = (v) =>
  v === "brownfield" ? "Brownfield" : v === "greenfield" ? "Greenfield" : v || "";

/**
 * LoadDemoProfileButton
 * ---------------------
 * A compact button + selector modal for loading a preseeded Demo Profile
 * into the SmartEvaluate flow. Visibility is controlled by the caller
 * (enabled only when demo mode is active).
 *
 * Props:
 *   - profiles: active profiles array (metadata only)
 *   - existingAssessmentId: string | null — if present, user will see
 *     a confirmation modal before existing data is overwritten.
 *   - existingEmail: string | null — same as above.
 *   - onApplied: ({ assessmentId, email, profile }) => void
 *   - variant: "compact" | "full" — visual density for the trigger button.
 */
export default function LoadDemoProfileButton({
  profiles,
  existingAssessmentId,
  existingEmail,
  onApplied,
  variant = "compact",
  preselectProfileId = null,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [applyingId, setApplyingId] = useState(null);
  const [pendingProfile, setPendingProfile] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState("");

  const list = profiles || [];

  // Deep-link from the admin Demo Profiles page ("Use Profile"): auto-open the
  // selector so the consultant reviews the pre-filled profile before applying.
  const [autoOpened, setAutoOpened] = useState(false);
  useEffect(() => {
    if (autoOpened || !preselectProfileId || !list.length) return;
    const match = list.find((p) => p.id === preselectProfileId);
    if (match) {
      setSearch(match.profile_name || "");
      setOpen(true);
      setAutoOpened(true);
    }
  }, [preselectProfileId, list, autoOpened]);

  const filtered = useMemo(() => {
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter((p) =>
      [p.profile_name, p.profile_description, p.representative_warehouse_type, p.scenario_type, p.complexity_label]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q)),
    );
  }, [list, search]);

  const startSelect = (p) => {
    setError("");
    // If there's an existing evaluation in progress, require confirmation before overwriting.
    if (existingAssessmentId || existingEmail) {
      setPendingProfile(p);
      setConfirmOpen(true);
      return;
    }
    apply(p);
  };

  const apply = async (p) => {
    setApplyingId(p.id);
    setError("");
    try {
      const full = await getDemoProfile(p.id);
      const md = full.module_data || {};
      const m1 = md.module1 || {};
      // 1. Create/refresh the assessment using Step 1 fields.
      //    createAssessment upserts by (email, in_progress), so if the user
      //    already had a draft on the same email, that one will be updated.
      const email = m1.email || "demo+" + full.id.slice(0, 8) + "@primes4.com";
      const createPayload = {
        company_name: m1.company_name || full.profile_name || "Demo Company",
        contact_name: m1.contact_name || "Demo User",
        email,
        phone: m1.phone || "+1 000 000 0000",
        role: m1.role || "",
        scenario_type:
          m1.scenario_type || full.scenario_type || "greenfield",
      };
      const a = await createAssessment(createPayload);
      // 2. Overwrite module_data.module2 and module_data.module3 on the new assessment.
      //    module_data is MERGED field-by-field on the backend, so we send a
      //    fresh assessment here — any prior module2/3 values on the same
      //    email's draft get replaced.
      // Warehouse Groups are the universal Step 3 unit. Demo profiles ship
      // warehouse_groups + grouped Step 3 answers (module3_groups); we no longer
      // send the legacy per-warehouse-type module3 block.
      const patch = {
        module_data: {
          module1: m1,
          module2: md.module2 || {},
          module3_groups: md.module3_groups || {},
        },
        warehouse_groups: full.warehouse_groups || [],
        current_step: 1,
        progress_percent: 33,
      };
      await updateAssessment(a.id, patch);
      // 3. Persist the session locally so Module 1/2/3 rehydrate correctly.
      try {
        localStorage.setItem("warehousex_email", email);
        localStorage.setItem("warehousex_assessment_id", a.id);
        localStorage.setItem("warehousex_demo_applied", full.profile_name || "");
      } catch {
        // localStorage can fail in private browsing — non-blocking.
      }
      setOpen(false);
      setConfirmOpen(false);
      setPendingProfile(null);
      if (onApplied) {
        onApplied({ assessmentId: a.id, email, profile: full });
      }
    } catch (e) {
      console.error("Demo profile apply failed:", e);
      setError("Couldn't load this profile — please try again.");
    } finally {
      setApplyingId(null);
    }
  };

  if (!list.length) return null;

  const buttonCls =
    variant === "compact"
      ? "inline-flex items-center gap-1.5 rounded-sm border border-[#025979]/25 bg-white text-[#025979] hover:bg-[#E0F2FE] px-3 py-1.5 text-xs font-medium transition-colors"
      : "inline-flex items-center gap-2 rounded-sm border border-[#025979] bg-white text-[#025979] hover:bg-[#E0F2FE] px-4 py-2 text-sm font-medium transition-colors";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={buttonCls}
        data-testid="load-demo-profile-btn"
      >
        <Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} />
        Load Demo Profile
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col p-0"
          data-testid="demo-profile-selector"
        >
          <DialogHeader className="px-6 pt-6 pb-3">
            <DialogTitle className="text-base font-semibold text-[#111827]" style={{ fontFamily: "Chivo, sans-serif" }}>
              Load Demo Profile
            </DialogTitle>
            <DialogDescription className="text-xs text-[#6B7280]">
              Select a preseeded scenario to prefill Steps 1, 2, and 3. You can edit everything after it loads.
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 pb-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9CA3AF]" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, scenario, or complexity…"
                className="pl-8 h-9 text-xs rounded-sm"
                data-testid="demo-profile-search"
              />
            </div>
          </div>

          <div className="px-6 pb-6 overflow-y-auto space-y-2" data-testid="demo-profile-list">
            {filtered.length === 0 ? (
              <div className="py-8 text-center text-xs text-[#9CA3AF]">
                No profiles match your search.
              </div>
            ) : (
              filtered.map((p) => {
                const busy = applyingId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => !applyingId && startSelect(p)}
                    disabled={!!applyingId}
                    className="w-full text-left rounded-sm border border-[#E5E7EB] bg-white p-3.5 hover:border-[#025979] hover:shadow-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid={`demo-profile-option-${p.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-sm font-semibold text-[#111827] truncate">
                            {p.profile_name}
                          </h4>
                          {p.default && (
                            <span className="px-1.5 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A] text-[10px] font-medium">
                              Default
                            </span>
                          )}
                        </div>
                        {p.profile_description && (
                          <p className="text-[11px] text-[#6B7280] mt-1 line-clamp-2">
                            {p.profile_description}
                          </p>
                        )}
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {p.scenario_type && (
                            <span className="px-2 py-0.5 rounded-full border text-[10px] bg-[#F0EDF9] text-[#5847B0] border-[#DDD6FE]">
                              {scenarioLabel(p.scenario_type)}
                            </span>
                          )}
                          {p.complexity_label && (
                            <span
                              className={`px-2 py-0.5 rounded-full border text-[10px] ${complexityStyle(p.complexity_label)}`}
                            >
                              {p.complexity_label} Complexity
                            </span>
                          )}
                          {p.representative_warehouse_type && (
                            <span className="px-2 py-0.5 rounded-full border text-[10px] bg-[#F9FAFB] text-[#374151] border-[#E5E7EB] truncate max-w-[220px]">
                              {p.representative_warehouse_type}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0 pt-1 text-[#025979]">
                        {busy ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <ArrowRight className="w-4 h-4 opacity-60" />
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {error && (
            <div className="px-6 pb-4">
              <p className="text-[11px] text-red-600" data-testid="demo-profile-error">
                {error}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation when overwriting an existing evaluation */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="demo-profile-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm font-semibold text-[#111827]">
              <AlertTriangle className="w-4 h-4 text-[#C2410C]" />
              Replace current inputs?
            </DialogTitle>
            <DialogDescription className="text-xs text-[#6B7280]">
              You already have evaluation data in progress. Loading
              <span className="font-semibold text-[#111827] mx-1">
                {pendingProfile?.profile_name || "this profile"}
              </span>
              will replace your current answers across all three steps. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              className="rounded-sm text-xs"
              onClick={() => {
                setConfirmOpen(false);
                setPendingProfile(null);
              }}
              data-testid="demo-profile-confirm-cancel"
            >
              Cancel
            </Button>
            <Button
              className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
              onClick={() => pendingProfile && apply(pendingProfile)}
              disabled={!!applyingId}
              data-testid="demo-profile-confirm-apply"
            >
              {applyingId ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Applying…
                </>
              ) : (
                <>
                  <Check className="w-3 h-3 mr-1" /> Confirm &amp; Replace
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * useDemoMode — returns whether the current session qualifies for the
 * "Load Demo Profile" button. Visibility is strictly role-based:
 *   enabled = true iff session role ∈ {"admin", "demo"}.
 * Fail-safe: if the backend call fails or no session is detected, the
 * button stays hidden.
 *
 * Returns { loading, enabled, role, profiles, refresh }.
 */
export function useDemoMode() {
  const [state, setState] = useState({ loading: true, enabled: false, role: null, profiles: [] });
  const refresh = () => {
    getDemoMode()
      .then((d) =>
        setState({
          loading: false,
          enabled: !!d?.enabled,
          role: d?.role || null,
          profiles: Array.isArray(d?.profiles) ? d.profiles : [],
        }),
      )
      .catch(() => setState({ loading: false, enabled: false, role: null, profiles: [] }));
  };
  useEffect(() => {
    refresh();
    // Re-check when the tab regains focus — picks up any session change
    // (e.g. admin logs in/out in another tab) without a hard reload.
    const onFocus = () => refresh();
    if (typeof window !== "undefined") {
      window.addEventListener("focus", onFocus);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("focus", onFocus);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { ...state, refresh };
}
